// src/components/dashboard/TicketsTable.jsx
import { useState } from 'react';
import styles from './TicketsTable.module.css';
import StatusBadge from './StatusBadge';
import TicketDetail from './TicketDetail';

/** Number of tickets shown per page. */
export const PAGE_SIZE = 7;

// ─────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────

/**
 * Formats an ISO date string as "Mon D, YYYY" (e.g. "Jul 14, 2026").
 *
 * @param {string|null} iso
 * @returns {string}
 */
function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return iso;
  }
}

/**
 * Returns the slice of tickets for the given page (1-indexed).
 *
 * @param {object[]} tickets - Full ticket list.
 * @param {number}   page    - Current page (1-indexed).
 * @param {number}   size    - Page size.
 * @returns {object[]}
 */
export function paginate(tickets, page, size) {
  const start = (page - 1) * size;
  return tickets.slice(start, start + size);
}

/**
 * Calculates total number of pages.
 *
 * @param {number} total - Total item count.
 * @param {number} size  - Page size.
 * @returns {number}
 */
export function totalPages(total, size) {
  return Math.max(1, Math.ceil(total / size));
}

// ─────────────────────────────────────────────────────────
// Small inner components
// ─────────────────────────────────────────────────────────

/**
 * Chevron icon that rotates when the row is expanded.
 *
 * @param {{ expanded: boolean }} props
 */
function ChevronIcon({ expanded }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${styles.chevron} ${expanded ? styles.chevronOpen : ''}`}
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * Loading skeleton row.
 */
function SkeletonRow() {
  return (
    <tr className={styles.skeletonRow}>
      {[1, 2, 3, 4, 5, 6, 7].map((i) => (
        <td key={i}>
          <span className={styles.skeletonCell} />
        </td>
      ))}
    </tr>
  );
}

/**
 * Full-width empty state displayed when there are no tickets.
 */
function EmptyState({ message }) {
  return (
    <tr>
      <td colSpan={7} className={styles.emptyCell}>
        <div className={styles.emptyState}>
          <svg
            className={styles.emptyIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
            <rect x="9" y="3" width="6" height="4" rx="1" />
          </svg>
          <p className={styles.emptyTitle}>{message || 'No open tickets'}</p>
          <p className={styles.emptyDesc}>
            {message
              ? 'Check back later or switch tabs to view other tickets.'
              : <>You have no active tickets. Use the <strong>Create Request</strong> section to raise one.</>}
          </p>
        </div>
      </td>
    </tr>
  );
}

/**
 * Full-width error state displayed when the API call fails.
 *
 * @param {{ message: string }} props
 */
function ErrorState({ message }) {
  return (
    <tr>
      <td colSpan={7} className={styles.emptyCell}>
        <div className={styles.errorState}>
          <svg
            className={styles.errorIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className={styles.emptyTitle}>Failed to load tickets</p>
          <p className={styles.emptyDesc}>{message}</p>
        </div>
      </td>
    </tr>
  );
}

/**
 * A single expandable ticket row plus its collapsible detail panel.
 *
 * @param {{ ticket: object }} props
 */
function TicketRow({ ticket }) {
  const [expanded, setExpanded] = useState(false);

  /** Toggles the expanded / collapsed detail panel. */
  const toggle = () => setExpanded((prev) => !prev);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <>
      <tr
        className={`${styles.row} ${expanded ? styles.rowExpanded : ''}`}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-label={`Ticket ${ticket.ticketId}, ${ticket.status}. Press Enter to ${expanded ? 'collapse' : 'expand'} details.`}
      >
        <td className={styles.expandCell}>
          <ChevronIcon expanded={expanded} />
        </td>
        <td className={styles.cell}>
          <span className={styles.ticketId}>{ticket.ticketId}</span>
        </td>
        <td className={styles.cell}>
          <span className={styles.loanId}>{ticket.loanId}</span>
        </td>
        <td className={styles.cell}>
          <StatusBadge status={ticket.status} />
        </td>
        <td className={styles.cell}>
          <span className={styles.system}>{ticket.impactedSystem}</span>
          {ticket.ticketType && ticket.ticketType !== '—' && (
            <span className={styles.ticketType}>{ticket.ticketType}</span>
          )}
        </td>
        <td className={styles.cell}>
          <span className={styles.assignee}>{ticket.assigneeDev}</span>
        </td>
        <td className={styles.cell}>
          <span className={styles.date}>{formatDate(ticket.createdAt)}</span>
        </td>
      </tr>

      {expanded && (
        <tr className={styles.detailRow} role="row">
          <td colSpan={7} className={styles.detailCell}>
            <TicketDetail ticket={ticket} />
          </td>
        </tr>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Pagination controls
// ─────────────────────────────────────────────────────────

/**
 * Renders previous / next page buttons and a "Page X of Y" indicator.
 *
 * @param {{ page: number, total: number, onPrev: Function, onNext: Function }} props
 */
export function Pagination({ page, total, onPrev, onNext }) {
  if (total <= 1) return null;

  return (
    <div className={styles.pagination} role="navigation" aria-label="Ticket pages">
      <button
        className={styles.pageBtn}
        onClick={onPrev}
        disabled={page === 1}
        aria-label="Previous page"
        data-testid="page-prev"
      >
        ‹ Prev
      </button>

      <span className={styles.pageInfo} data-testid="page-info">
        Page <strong>{page}</strong> of <strong>{total}</strong>
      </span>

      <button
        className={styles.pageBtn}
        onClick={onNext}
        disabled={page === total}
        aria-label="Next page"
        data-testid="page-next"
      >
        Next ›
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Main exported component
// ─────────────────────────────────────────────────────────

/**
 * Tickets table with frontend pagination (PAGE_SIZE rows per page).
 *
 * States handled:
 *   - loading  → skeleton rows
 *   - error    → error state row
 *   - empty    → empty state row
 *   - data     → paginated ticket rows + Pagination controls
 *
 * @param {{ tickets: object[], loading: boolean, error: string|null }} props
 */
export default function TicketsTable({ tickets, loading, error, emptyMessage }) {
  const [page, setPage] = useState(1);

  const numPages = totalPages(tickets.length, PAGE_SIZE);

  /** Advances to the next page (clamped to numPages). */
  const goNext = () => setPage((p) => Math.min(p + 1, numPages));

  /** Goes back to the previous page (clamped to 1). */
  const goPrev = () => setPage((p) => Math.max(p - 1, 1));

  const visibleTickets = paginate(tickets, page, PAGE_SIZE);

  const renderTableBody = () => {
    if (loading) {
      return (
        <>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </>
      );
    }
    if (error) {
      return <ErrorState message={error} />;
    }
    if (tickets.length === 0) {
      return <EmptyState message={emptyMessage} />;
    }
    return visibleTickets.map((ticket) => (
      <TicketRow key={ticket._key ?? ticket.ticketId} ticket={ticket} />
    ));
  };

  return (
    <div>
      <div className={styles.tableWrapper}>
        <table className={styles.table} role="table" aria-label="My tickets">
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.th} style={{ width: 36 }} aria-label="Expand" />
              <th className={styles.th}>Ticket ID</th>
              <th className={styles.th}>Loan ID</th>
              <th className={styles.th}>Status</th>
              <th className={styles.th}>System / Type</th>
              <th className={styles.th}>Assigned To</th>
              <th className={styles.th}>Created</th>
            </tr>
          </thead>
          <tbody>
            {renderTableBody()}
          </tbody>
        </table>
      </div>

      {/* Pagination — only rendered when there is data and more than one page */}
      {!loading && !error && tickets.length > 0 && (
        <Pagination
          page={page}
          total={numPages}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </div>
  );
}
